import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItem,
  Stack,
  SvgIcon,
} from '@mui/material'
import { useGetCustomerRequests } from './hooks/useGetCustomerRequests'
import { useCallback, useMemo, useState } from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { Customer, CustomerRequest } from '~/supabase/types'
import { dialogStyles } from '~/ui/styles/common'
import * as R from 'remeda'
import { useForm } from 'react-hook-form'
import { ControlledAutocomplete } from '~/ui/components/ControlledAutocomplete'
import { useGetCustomers } from './hooks/useGetCustomers'
import { useGetModels } from '../hooks/useGetModels'
import { ControlledTextField } from '~/ui/components/ControlledTextField'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCreateCustomerRequest } from '~/features/customers/hooks/useCreateCustomerRequest'
import { customerRequestTypes, customerRequestStatuses } from '~/features/customers/constants'
import { RequestStatusMenu } from '~/features/customers/components/RequestStatusMenu'
import { WaitlistActionMenu } from '~/features/customers/components/WaitlistActionMenu'
import { format } from 'date-fns'
import { IconX } from '@tabler/icons-react'
import { useDeleteCustomerRequests } from '~/features/customers/hooks/useDeleteCustomerRequests'
import { useDataGridProps } from '~/ui/hooks/useDataGridProps'
import { CustomDataGrid } from '~/ui/components/CustomDataGrid'
import { SelectionPanel, SelectionPanelAction } from '~/ui/components/SelectionPanel'
import { SimpleDialog } from '~/ui/components/SimpleDialog'

type Row = CustomerRequest &
  Omit<Customer, 'id' | 'created_at'> & { customerName: string | number | null }

export const CustomerRequestList = () => {
  const { data, isLoading } = useGetCustomerRequests()

  const [show, setShow] = useState(false)

  const { data: models, isLoading: isModelsLoading } = useGetModels()

  const modelsMap = useMemo(() => {
    if (!models) return {}

    return R.fromEntries(models.map((m) => [m.id.toString(), m] as const))
  }, [models])

  const columns = useMemo<GridColDef<Row>[]>(() => {
    if (!data) return []

    return [
      {
        field: 'created_at',
        headerName: 'Created at',
        valueGetter: (value: unknown) => {
          const parsed = z.coerce.date().safeParse(value)
          return parsed.success ? parsed.data : null
        },
        valueFormatter: (value: Date | null) => {
          return value ? format(value, 'dd-MMM-yyyy') : 'n/a'
        },
        width: 120,
      },
      {
        field: 'status',
        headerName: 'Status',
        renderCell: (params: GridRenderCellParams<Pick<CustomerRequest, 'status'>>) => (
          <Stack alignItems="center" justifyContent="flex-start" height={1} direction="row" gap={1}>
            <Chip
              label={params.row.status}
              color={
                params.row.status === 'failed'
                  ? 'error'
                  : params.row.status === 'complete'
                    ? 'success'
                    : 'warning'
              }
              size="small"
            />
          </Stack>
        ),
        width: 100,
      },
      { field: 'request_type', headerName: 'Type' },
      {
        field: 'customerName',
        headerName: 'Customer',
        width: 150,
      },
      {
        field: 'products',
        headerName: 'Products',
        renderCell: (params: GridRenderCellParams<Pick<CustomerRequest, 'products'>>) => {
          const modelList = params.row.products?.map((p) => modelsMap[p]?.stock_code ?? 'n/a')
          return (
            <Stack
              alignItems="center"
              justifyContent="flex-start"
              height={1}
              direction="row"
              gap={1}
            >
              {modelList?.map((p, i) => (
                <Stack key={i} direction="row" gap={0.5} alignItems="center">
                  <Chip label={p} size="small" />
                </Stack>
              )) ?? 'n/a'}
            </Stack>
          )
        },
        width: 120,
      },
      {
        field: 'note',
        headerName: 'Note',
        flex: 1,
      },
      {
        resizable: false,
        width: 120,
        field: 'actions',
        type: 'actions',
        getActions: (params) => {
          if (params.row.request_type === 'waitlist' && params.row.email) {
            const model = R.pipe(
              params.row.products ?? [],
              R.map((p) => modelsMap[p]),
              R.filter(R.isNonNullish),
              R.first()
            )

            return [
              <WaitlistActionMenu
                key={params.id}
                customerName={params.row.full_name}
                email={params.row.email}
                country={params.row.country}
                model={model}
                requestId={params.row.id}
              />,
            ]
          }
          return [<RequestStatusMenu key={params.id} id={params.row.id} />]
        },
      },
    ]
  }, [data, modelsMap])

  const rows = useMemo<Row[]>(() => {
    if (!data) return []

    return R.pipe(
      data,
      R.map((d) => {
        const trimmed = R.omit(d.customers, ['created_at', 'id'])

        return R.omit(
          { ...d, ...trimmed, customerName: trimmed.full_name || trimmed.email || d.customers.id },
          ['customers']
        )
      }),
      R.sortBy([R.prop('created_at'), 'desc'])
    )
  }, [data])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CustomerRequest>({
    defaultValues: {
      customer: 0,
      products: null,
      note: null,
      status: 'pending',
      request_type: null,
    },
    resolver: zodResolver(
      z.object({
        customer: z.number(),
        products: z.array(z.string()),
        note: z.string(),
        request_type: z.enum(customerRequestTypes),
        status: z.enum(customerRequestStatuses),
      })
    ),
  })

  const handleClose = () => {
    setShow(false)
    reset()
  }

  const { data: customers, isLoading: isCustomersLoading } = useGetCustomers({ enabled: show })

  const { mutate, isPending } = useCreateCustomerRequest()
  const { mutate: deleteCustomerRequests, isPending: isDeletingRequest } =
    useDeleteCustomerRequests()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const {
    selectedRows,
    onRowSelectionModelChange,
    selectionModel,
    reset: resetSelection,
  } = useDataGridProps({ rows })

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false)
  }, [])

  const confirmDelete = useCallback(() => {
    if (selectedRows.length === 0) return

    deleteCustomerRequests(
      selectedRows.map((r) => r.id),
      {
        onSuccess: () => {
          setIsDeleteDialogOpen(false)
          resetSelection()
        },
      }
    )
  }, [selectedRows, deleteCustomerRequests, resetSelection])

  const selectionActions: SelectionPanelAction[] = useMemo(() => {
    return [
      {
        key: 'delete',
        Component: () => (
          <Button
            onClick={() => setIsDeleteDialogOpen(true)}
            disabled={isDeletingRequest}
            startIcon={isDeletingRequest ? <CircularProgress size={14} /> : null}
          >
            Delete
          </Button>
        ),
      },
    ]
  }, [isDeletingRequest])

  const createRequest = (data: Omit<CustomerRequest, 'id' | 'created_at'>) => {
    mutate(data, {
      onSuccess: () => {
        handleClose()
      },
    })
  }

  return (
    <Stack flex={1}>
      <Stack>
        <Button sx={{ alignSelf: 'flex-end' }} variant="contained" onClick={() => setShow(true)}>
          Create new
        </Button>
      </Stack>

      <SimpleDialog
        open={isDeleteDialogOpen}
        title="Delete Request"
        text={`Are you sure you want to delete ${selectedRows.length} request${selectedRows.length > 1 ? 's' : ''}?`}
        description="This action cannot be undone."
        onSubmit={confirmDelete}
        onClose={handleCloseDeleteDialog}
        isLoading={isDeletingRequest}
        color="error"
        confirmText="Delete"
      />
      <Stack position="relative" flex={1}>
        <CustomDataGrid
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          rows={rows}
          columns={columns}
          loading={isLoading}
        >
          <SelectionPanel rows={selectedRows} actions={selectionActions} onClose={resetSelection} />
        </CustomDataGrid>
      </Stack>

      <Dialog open={show} onClose={handleClose} PaperProps={{ sx: dialogStyles }}>
        <Stack component="form" onSubmit={handleSubmit(createRequest)}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <DialogTitle>Create a request</DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: 2 }}>
              <SvgIcon>
                <IconX />
              </SvgIcon>
            </IconButton>
          </Stack>
          <DialogContent>
            <Stack gap={2}>
              <ControlledAutocomplete
                required
                errors={errors}
                control={control}
                name="customer"
                options={customers?.map((c) => c.id) ?? []}
                label="Customer"
                loading={isCustomersLoading}
                getOptionLabel={(value) => {
                  const one = customers?.find((c) => c.id === value)

                  return one?.full_name ?? value.toString()
                }}
                getOptionKey={(id) => id}
              />
              <ControlledAutocomplete
                required
                errors={errors}
                control={control}
                name="products"
                options={models?.map((c) => c.id.toString()) ?? []}
                renderOption={(props, id) => {
                  const model = modelsMap[id]
                  return (
                    <ListItem {...props} key={id}>
                      {model ? model.stock_code : 'n/a'}
                    </ListItem>
                  )
                }}
                getOptionLabel={(id) => {
                  const model = modelsMap[id]

                  return model ? model.stock_code : 'n/a'
                }}
                label="Model"
                loading={isModelsLoading}
                multiple
              />
              <ControlledAutocomplete
                required
                errors={errors}
                control={control}
                name="request_type"
                options={customerRequestTypes}
                label="Type"
              />
              <ControlledTextField
                control={control}
                name="note"
                errors={errors}
                multiline
                label="Note"
              />
              <Button
                variant="contained"
                type="submit"
                endIcon={isPending ? <CircularProgress size={14} /> : null}
                disabled={isPending}
              >
                Create
              </Button>
            </Stack>
          </DialogContent>
        </Stack>
      </Dialog>
    </Stack>
  )
}
